// Wrappers for the tutor-side BMD assignment endpoints, all under the
// `/bmd/*` route set in tutor-backend.
//
// Use the shared `apiClient` from `src/infra/api-client.ts`, not raw axios:
// its interceptors handle Bearer-token injection and the expired-token
// redirect.

import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type {
  BmdTopicsForYearRequest,
  BmdTopicsForYearResponse,
  BmdStrandsForYearRequest,
  BmdStrandsForYearResponse,
  BmdYearLevelsRequest,
  BmdYearLevelsResponse,
  BmdPreviewQuestionsRequest,
  BmdPreviewQuestionsResponse,
  BmdAssignedPreviewRequest,
  BmdAssignedPreviewResponse,
  BmdAssignmentForSessionRequest,
  BmdAssignmentForSessionResponse,
  BmdCreatePayload,
  BmdCreateResponse,
} from "../types/bmd.types"

const GET_YEAR_LEVELS_ENDPOINT = "bmd/getYearLevels"
const GET_TOPICS_FOR_YEAR_ENDPOINT = "bmd/getTopicsForYear"
const GET_STRANDS_FOR_YEAR_ENDPOINT = "bmd/getStrandsForYear"
const PREVIEW_QUESTIONS_ENDPOINT = "bmd/previewQuestions"
const PREVIEW_ASSIGNED_ENDPOINT = "bmd/previewAssignedActivity"
const GET_EXISTING_FOR_SESSION_ENDPOINT = "bmd/getExistingForSession"
const CREATE_ASSIGNMENT_ENDPOINT = "bmd/createAssignment"

export async function getBmdYearLevels(
  req: BmdYearLevelsRequest,
  signal?: AbortSignal,
): Promise<BmdYearLevelsResponse> {
  const response = await apiClient.post<BmdYearLevelsResponse>(
    BASE_URL + GET_YEAR_LEVELS_ENDPOINT,
    {
      subject_id: req.subject_id,
      student_key: req.student_key,
    },
    { signal },
  )
  const data = response.data
  return {
    year_levels: Array.isArray(data?.year_levels) ? data.year_levels : [],
  }
}

export async function getBmdTopicsForYear(
  req: BmdTopicsForYearRequest,
  signal?: AbortSignal,
): Promise<BmdTopicsForYearResponse> {
  const response = await apiClient.post<BmdTopicsForYearResponse>(
    BASE_URL + GET_TOPICS_FOR_YEAR_ENDPOINT,
    {
      year_level_id: req.year_level_id,
      subject_id: req.subject_id,
      student_key: req.student_key,
    },
    { signal },
  )
  const data = response.data
  return {
    year_level_id: data?.year_level_id ?? req.year_level_id,
    topics: Array.isArray(data?.topics) ? data.topics : [],
    locked_topic_ids: Array.isArray(data?.locked_topic_ids)
      ? data.locked_topic_ids
      : [],
  }
}

export async function getBmdStrandsForYear(
  req: BmdStrandsForYearRequest,
  signal?: AbortSignal,
): Promise<BmdStrandsForYearResponse> {
  const response = await apiClient.post<BmdStrandsForYearResponse>(
    BASE_URL + GET_STRANDS_FOR_YEAR_ENDPOINT,
    {
      year_level_id: req.year_level_id,
      subject_id: req.subject_id,
      student_key: req.student_key,
    },
    { signal },
  )
  const data = response.data
  return {
    year_level_id: data?.year_level_id ?? req.year_level_id,
    strands: Array.isArray(data?.strands) ? data.strands : [],
    locked_topic_ids: Array.isArray(data?.locked_topic_ids)
      ? data.locked_topic_ids
      : [],
  }
}

export async function getBmdPreviewQuestions(
  req: BmdPreviewQuestionsRequest,
  signal?: AbortSignal,
): Promise<BmdPreviewQuestionsResponse> {
  const response = await apiClient.post<BmdPreviewQuestionsResponse>(
    BASE_URL + PREVIEW_QUESTIONS_ENDPOINT,
    {
      year_level_id: req.year_level_id,
      subject_id: req.subject_id,
      topic_ids: req.topic_ids ?? [],
    },
    { signal },
  )
  const data = response.data
  return {
    year_level_id: data?.year_level_id ?? req.year_level_id,
    question_count: Number(data?.question_count) || 0,
    questions: Array.isArray(data?.questions) ? data.questions : [],
  }
}

/**
 * Preview of a test that has ALREADY been assigned, read from the session's own
 * question rows.
 *
 * Not the same as getBmdPreviewQuestions, which builds a preview from a year
 * and a set of topics. That cannot describe an assigned test: the content may
 * have changed since, and a prerequisite test has no year or topic to derive
 * from at all, being built from skills. The server returns the same shape
 * either way, so one modal renders both.
 */
export async function getBmdAssignedActivityPreview(
  req: BmdAssignedPreviewRequest,
  signal?: AbortSignal,
): Promise<BmdAssignedPreviewResponse> {
  const response = await apiClient.post<BmdAssignedPreviewResponse>(
    BASE_URL + PREVIEW_ASSIGNED_ENDPOINT,
    {
      pk_activity_key: req.pk_activity_key,
      pk_student_key: req.pk_student_key,
    },
    { signal },
  )
  const data = response.data
  return {
    year_level_id: data?.year_level_id ?? null,
    flow_status: data?.flow_status ?? null,
    is_prereq_test: !!data?.is_prereq_test,
    question_count: Number(data?.question_count) || 0,
    questions: Array.isArray(data?.questions) ? data.questions : [],
  }
}

export async function createBmdAssignment(
  req: BmdCreatePayload,
): Promise<BmdCreateResponse> {
  const response = await apiClient.post<BmdCreateResponse>(
    BASE_URL + CREATE_ASSIGNMENT_ENDPOINT,
    {
      session_key: req.session_key,
      student_key: req.student_key,
      lesson_key: req.lesson_key,
      is_homework: req.is_homework,
      subject_id: req.subject_id,
      year_level_id: req.year_level_id,
      topic_ids: req.topic_ids,
      force_new: req.force_new || false,
    },
  )
  return response.data
}

export async function getBmdAssignmentForSession(
  req: BmdAssignmentForSessionRequest,
  signal?: AbortSignal,
): Promise<BmdAssignmentForSessionResponse> {
  const response = await apiClient.post<BmdAssignmentForSessionResponse>(
    BASE_URL + GET_EXISTING_FOR_SESSION_ENDPOINT,
    {
      session_key: req.session_key,
      student_key: req.student_key,
    },
    { signal },
  )
  const data = response.data
  return {
    assignment: null,
    assignments: Array.isArray(data?.assignments)
      ? data.assignments
      : [],
  }
}

